/**
 * Campo de formulario de Staff: etiqueta arriba (16 px/500, mínimo tipográfico de §8.1), texto de
 * ayuda (14 px) y error (14 px con icono), conectados por `aria-describedby` / `aria-invalid`
 * (design-system §7 y §10). Misma API que el `Field` del Admin (render prop con los atributos).
 */
import { CircleAlert } from 'lucide-react';
import { useId, type ReactNode } from 'react';

import { cn } from './cn';

export interface FieldControlProps {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': true | undefined;
  /** 007 · T077: el asterisco es decorativo (`aria-hidden`); lo obligatorio se anuncia aquí. */
  'aria-required': true | undefined;
}

export interface FieldProps {
  label: string;
  /** Recibe los atributos que debe llevar el control (id, aria-*). */
  children: (controlProps: FieldControlProps) => ReactNode;
  help?: string;
  error?: string;
  required?: boolean;
  className?: string;
}

export function Field({ label, children, help, error, required, className }: FieldProps) {
  const id = useId();
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('flex flex-col gap-fm-2', className)}>
      <label htmlFor={id} className="text-fm-base font-medium text-fm-text">
        {label}
        {required ? (
          <span className="text-fm-primary-text" aria-hidden="true">
            {' *'}
          </span>
        ) : null}
      </label>

      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        'aria-required': required ? true : undefined,
      })}

      {help ? (
        <p id={helpId} className="text-fm-sm text-fm-text-secondary">
          {help}
        </p>
      ) : null}

      {error ? (
        <p
          id={errorId}
          aria-live="polite"
          className="flex items-center gap-fm-1 text-fm-sm text-fm-danger-text"
        >
          <CircleAlert size={16} aria-hidden="true" />
          {error}
        </p>
      ) : null}
    </div>
  );
}
